import { Switch, SwitchProps, useTheme } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";
import { Controller } from "react-hook-form";
interface ToggleProps extends Omit<SwitchProps, "onChange" | "disabled"> {
  name?: string;
  isChecked: boolean;
  isDisabled?: boolean;
  onChange: (value: boolean) => void;
  onClick?: (e: React.SyntheticEvent) => void;
  cursorStyle?: string;
  control?: any;
}

export const ToggleButton = ({
  name = "toggle",
  isChecked,
  isDisabled,
  onChange,
  onClick,
  cursorStyle,
  control,
  ...rest
}: ToggleProps) => {
  //   const theme = useTheme();

  return control ? (
    <Controller
      control={control}
      name={name}
      render={({ field: { ref, onChange, onBlur, value, name } }) => (
        <CustomToggleButton
          ref={ref}
          onBlur={onBlur}
          value={value}
          name={name}
          onChange={onChange}
          isChecked={isChecked}
          isDisabled={isDisabled}
          onClick={onClick}
          cursorStyle={cursorStyle}
          {...rest}
        />
      )}
    />
  ) : (
    <CustomToggleButton
      name={name}
      isChecked={isChecked}
      isDisabled={isDisabled}
      onChange={onChange}
      onClick={onClick}
      cursorStyle={cursorStyle}
      {...rest}
    />
  );
};

const CustomToggleButton = ({
  name,
  isChecked,
  isDisabled,
  onChange,
  onClick,
  cursorStyle,
  ...rest
}: ToggleProps) => {
  const handleClick = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    onChange(checked);
  };
  return (
    <Switch
      onChange={handleClick}
      onClick={onClick}
      checked={isChecked === true}
      focusVisibleClassName=".Mui-focusVisible"
      disableRipple
      disabled={isDisabled}
      sx={(theme) => ({
        height: {
          xs: "24px",
        },
        width: {
          xs: "40px",
        },
        padding: 0,
        "& .MuiSwitch-switchBase": {
          cursor: cursorStyle || "pointer",
          padding: 0,
          transitionDuration: "300ms",
          "&.Mui-checked": {
            transform: {
              xs: "translateX(16px)",
            },
            color: "#fff",
            "& + .MuiSwitch-track": {
              backgroundColor: "#B7BDFB",
              opacity: 1,
              border: 0, //`.6px solid ${colorPalette.sematics.lightBg.success}`
            },
            "& .MuiSwitch-thumb": {
              backgroundColor: theme.palette.purple.main,
              border: 0,
              boxShadow: "-2px 0px 3px #838ACD",
              backgroundImage: whiteTrackLines,
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: 1,
            },
          },
          // '&.Mui-focusVisible .MuiSwitch-thumb': {     color: '#33cf4d',     border:
          // '2px solid #fff', },
          "&.Mui-disabled .MuiSwitch-thumb": {
            cursor: "not-allowed",
            backgroundColor: isChecked
              ? theme.palette.secondary["purple-tint-2"]
              : "#FFF",
            boxShadow: isChecked
              ? "-2px 0px 4px 0px #5E6CFF40"
              : "2px 2px 3px 0px #0000001A",
            border: isChecked
              ? "none"
              : `1px solid ${theme.palette.neutral["010"]}`,
            backgroundImage: isChecked ? whiteTrackLines : neutral15TrackLines,
          },
          "&.Mui-disabled + .MuiSwitch-track": {
            opacity: 1,
            backgroundColor: isChecked
              ? theme.palette.secondary["purple-tint-3"]
              : theme.palette.neutral["010"],
            border: `1px solid ${theme.palette.neutral["010"]}`,
          },
        },
        "& .MuiSwitch-thumb": {
          boxSizing: "border-box",
          height: {
            xs: "24px",
          },
          width: {
            xs: "24px",
          },
          boxShadow: "2px 0px 3px #0000001A",
          border: `1px solid ${theme.palette.neutral["020"]}`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundImage: neutral20TrackLines,
        },
        "& .MuiSwitch-track": {
          border: `.6px solid ${theme.palette.neutral["020"]}`,
          borderRadius: 26 / 2,
          backgroundColor: "#D9D9D9",
          opacity: 1,
          transition: theme.transitions.create(["background-color"], {
            duration: 500,
          }),
        },
      })}
      size="small"
      {...rest}
    />
  );
};

const whiteTrackLines = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="5" width="6" viewBox="0 0 5 6"><line x1="0.5" y1="0.5" x2="0.5" y2="5.5" stroke="${encodeURIComponent(
  "#FFF",
)}" strokeLinecap="round"/><line x1="2.5" y1="0.5" x2="2.5" y2="5.5" stroke="${encodeURIComponent(
  "#FFF",
)}" strokeLinecap="round"/><line x1="4.5" y1="0.5" x2="4.5" y2="5.5" stroke="${encodeURIComponent(
  "#FFF",
)}" strokeLinecap="round"/></svg>')`;

const neutral20TrackLines = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="5" width="6" viewBox="0 0 5 6"><line x1="0.5" y1="0.5" x2="0.5" y2="5.5" stroke="${encodeURIComponent(
  "#CCCCCC",
)}" strokeLinecap="round"/><line x1="2.5" y1="0.5" x2="2.5" y2="5.5" stroke="${encodeURIComponent(
  "#CCCCCC",
)}" strokeLinecap="round"/><line x1="4.5" y1="0.5" x2="4.5" y2="5.5" stroke="${encodeURIComponent(
  "#CCCCCC",
)}" strokeLinecap="round"/></svg>')`;

const neutral15TrackLines = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="5" width="6" viewBox="0 0 5 6"><line x1="0.5" y1="0.5" x2="0.5" y2="5.5" stroke="${encodeURIComponent(
  "#E3E3E3",
)}" strokeLinecap="round"/><line x1="2.5" y1="0.5" x2="2.5" y2="5.5" stroke="${encodeURIComponent(
  "#E3E3E3",
)}" strokeLinecap="round"/><line x1="4.5" y1="0.5" x2="4.5" y2="5.5" stroke="${encodeURIComponent(
  "#E3E3E3",
)}" strokeLinecap="round"/></svg>')`;
